import { motion } from 'motion/react';
import { format } from 'date-fns';
import { isGameLive } from '../../lib/game-utils';
import { Trash2, Trophy, Clock, ChevronRight, Pencil, Check, BarChart3, Layout, PlayCircle, Play, Sparkles } from 'lucide-react';

export const GameCard = ({ game, isHeadCoach, onDelete, onStart, onResume, onPrepare, onEdit, onEditGame, onOpenTournament, onImportStats, onViewInsights, onEnterResults }: { game: any, isHeadCoach?: boolean, onDelete: (id: string) => void, onStart: (game: any) => void, onResume: (game: any) => void, onPrepare: (game: any) => void, onEdit: (game: any) => void, onEditGame?: (game: any) => void, onOpenTournament?: (id: string) => void, onImportStats?: (game: any) => void, onViewInsights?: (game: any) => void, onEnterResults?: (game: any) => void, key?: any }) => {
  const gameDate = new Date(game.date);
  const now = new Date();
  const isPast = gameDate < now;
  const isMoreThanOneDayPast = (now.getTime() - gameDate.getTime()) > (24 * 60 * 60 * 1000);
  const isInProgress = isGameLive(game);

  return (
    // The shadow lives on this wrapper: overflow-hidden would clip it on the card.
    <div className="relative overflow-hidden group rounded-card shadow-card">
      {/* Delete Action (Revealed on Swipe — head coach only). Inset 1px so the
          red can't bleed through the card's anti-aliased corners at rest. */}
      {isHeadCoach !== false && (
        <div style={{ position: 'absolute', inset: '1px', backgroundColor: '#E5484D', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '28px', borderRadius: '16px' }}>
          <button
            onClick={() => onDelete(game.id)}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: '#FFFFFF', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <Trash2 style={{ width: '20px', height: '20px' }} />
            <span style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Delete</span>
          </button>
        </div>
      )}

      {/* Main Card Content */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -100, right: 0 }}
        dragElastic={0.1}
        style={{
          padding: '16px',
          borderRadius: '16px',
          borderLeft: game.tournamentId ? '2.5px solid #C9A84C' : undefined,
          backgroundColor: '#FFFFFF',
          position: 'relative',
          zIndex: 10,
        }}
        className="cursor-grab active:cursor-grabbing"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
              {game.tournamentId
                ? <Trophy style={{ width: '12px', height: '12px', color: '#C9A84C' }} />
                : <Clock style={{ width: '12px', height: '12px', color: '#9AA5B1' }} />}
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                {format(new Date(game.date), 'MMM d • h:mm a')}
              </span>
            </div>
            <h3 style={{ fontSize: '17px', fontWeight: 600, color: '#141A21', lineHeight: 1.2 }}>
              {game.isHome ? 'vs ' : '@ '}{game.opponent}
            </h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {game.tournamentId && (
                <button
                  onClick={() => onOpenTournament?.(game.tournamentId)}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <span style={{ fontSize: '10px', fontWeight: 600, color: '#B8912E', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tournament</span>
                  <ChevronRight style={{ width: '10px', height: '10px', color: '#B8912E' }} />
                </button>
              )}
              {game.status !== 'completed' && (
                <button
                  onClick={() => onEdit(game)}
                  style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}
                >
                  <Pencil style={{ width: '12px', height: '12px', color: '#9AA5B1' }} />
                </button>
              )}
            </div>
            {game.status === 'completed' && game.result ? (
              <span style={{
                fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap',
                color: game.result.outcome === 'L' ? '#E5484D' : '#141A21',
              }}>
                {game.result.outcome} {game.result.myScore}–{game.result.opponentScore}
              </span>
            ) : null}
            {game.plan?.isReady && game.status !== 'completed' ? (
              <span style={{ fontSize: '10px', fontWeight: 600, color: '#141A21', display: 'flex', alignItems: 'center', gap: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <Check style={{ width: '11px', height: '11px' }} /> Plan Ready
              </span>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '0.5px solid #E3E7EB' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {game.status === 'completed' ? (
              <>
                <button
                  onClick={() => onEditGame?.(game)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '0.5px solid #9AA5B1', backgroundColor: 'transparent', fontSize: '12px', fontWeight: 600, color: '#141A21', cursor: 'pointer' }}
                >
                  <Pencil style={{ width: '12px', height: '12px' }} />
                  Edit Game
                </button>
                <button
                  onClick={() => onImportStats?.(game)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '0.5px solid #9AA5B1', backgroundColor: 'transparent', fontSize: '12px', fontWeight: 600, color: '#141A21', cursor: 'pointer' }}
                >
                  <BarChart3 style={{ width: '12px', height: '12px' }} />
                  {game.stats && Object.keys(game.stats).length > 0 ? 'Box Score' : game.gcImport ? 'Game Data' : 'Import Data'}
                </button>
                {onViewInsights && (
                  <button
                    onClick={() => onViewInsights(game)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '0.5px solid #9AA5B1', backgroundColor: 'transparent', fontSize: '12px', fontWeight: 600, color: '#141A21', cursor: 'pointer' }}
                  >
                    <Sparkles style={{ width: '12px', height: '12px' }} />
                    Insights
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => onPrepare(game)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '0.5px solid #9AA5B1', backgroundColor: 'transparent', fontSize: '12px', fontWeight: 600, color: '#141A21', cursor: 'pointer' }}
                >
                  <Layout style={{ width: '12px', height: '12px' }} />
                  Plan
                </button>
                <button
                  onClick={() => isInProgress ? onResume(game) : onStart(game)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: 'none', backgroundColor: '#141A21', fontSize: '12px', fontWeight: 600, color: '#FFFFFF', cursor: 'pointer' }}
                >
                  {isInProgress
                    ? <PlayCircle style={{ width: '12px', height: '12px' }} />
                    : <Play style={{ width: '12px', height: '12px' }} />}
                  {isInProgress ? 'Resume Game' : 'Start Game'}
                </button>
                {isPast && onEnterResults && (
                  <button
                    onClick={() => onEnterResults(game)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '0.5px solid #9AA5B1', backgroundColor: 'transparent', fontSize: '12px', fontWeight: 600, color: '#5A6672', cursor: 'pointer' }}
                  >
                    <Check style={{ width: '12px', height: '12px' }} />
                    Enter Results
                  </button>
                )}
              </>
            )}
          </div>
          {game.status === 'completed' && (
            <span style={{ fontSize: '10px', fontWeight: 600, color: '#9AA5B1', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Final</span>
          )}
        </div>
      </motion.div>
    </div>
  );
};
